import React, { useContext, useEffect, useState } from "react";
import {
  Acknowledgement,
  AddButton,
  BusinessText,
  BusinessTitle,
  DirectorIndex,
  IconWrapper,
  OnboardingButtonContainer,
  OverallContainer,
  PageHeaderWrapper,
} from "../styles";
import FormSection from "../components/FormSection";
import CustomDatePicker from "../../../components/common/CustomDateRangePicker/CustomDatePicker";
import DeleteIcon from "../icons/delete.svg";
import PlusIcon from "../icons/plus.svg";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { DirectorTypes } from "../Types";
import { Button } from "../../../components/common/Button";
import { addBusinessDirector, deleteDirector, getParticularStep } from "../redux/actions";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import Loader from "../../../components/common/Loader";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { Input } from "../../../components/common/Input";
import { updateDirectorsList } from "../redux/slice";
import { useWindowSize } from "@reach/window-size";
import { formatDate } from "../../../utils/dateUtils";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { isUnderage } from "../utils/helperFunctions";
import { useBusinessTypes } from "../../../app/hooks/useBusinessTypes";
import { InputPhone } from "../../../components/common/InputPhone";

dayjs.locale("en");

type Props = {
  handleNext: () => void;
  handlePrevious: () => void;
};

const getNestedError = (errors: any, path: string) => {
  return path.split(".").reduce((obj, key) => obj?.[key], errors);
};

const Director = ({ handleNext, handlePrevious }: Props) => {
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    reset,
    watch,
  } = useForm<DirectorTypes>({
    defaultValues: {
      ubo: {
        id: null,
        name: "",
        email: "",
        phone: "",
        dateOfBirth: null,
        ownershipPercentage: "",
      },
      isUboDirector: false,
      directors: [{ id: null, name: "", email: "", dateOfBirth: null }],
    },
    mode: "onChange",
  });

  const dispatch = useAppDispatch();
  const { width } = useWindowSize();

  const { businessTypes } = useBusinessTypes();
  const [selectedBusinessType, setSelectedBusinessType] = useState<any>(null);
  const {
    businessId,
    userObj
  } = useAppSelector((state) => state.users);
  const businessCountry = userObj?.accountUser?.businessCountry;

  const { businessTypeId, loading } = useAppSelector(
    (state) => state.onboarding || {}
  );
  const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};

  const { fields, append, remove } = useFieldArray({
    control,
    name: "directors",
  });

  const isUboDirector = watch("isUboDirector");

  const addDirector = () => {
    const maxDirectors = isUboDirector ? 2 : 3;
    if (fields.length < maxDirectors) {
      append({ name: "", email: "", dateOfBirth: null });
    }
  };

  const handleDeleteDirector = async (index: number) => {
    const directors = watch("directors");
    const director = directors?.[index];

    if (director?.id) {
      const action = await dispatch(
        deleteDirector({ directorId: director.id, businessId })
      );
      if (deleteDirector.rejected.match(action)) {
        onShowErrorAlert("", String(action.payload));
        return;
      }
    }

    remove(index);
  };


  useEffect(() => {
    const getSelectedBusinessType = () => {
      const selectedBizType = businessTypes.find(
        (type) => type.value === businessTypeId
      );
      setSelectedBusinessType(selectedBizType);
    };
    getSelectedBusinessType();
  }, [businessTypeId, businessTypes]);

  const handleAddDirector = async (data: DirectorTypes) => {
    const payload: any[] = [];

    if (data.ubo) {
      payload.push({
        ...(data.ubo.id ? { id: data.ubo.id } : {}),
        businessId,
        name: data.ubo.name,
        email: data.ubo.email,
        phone: data.ubo.phone || null,
        dob: data.ubo.dateOfBirth ? formatDate(data.ubo.dateOfBirth) : null,
        isDirector: Boolean(data.isUboDirector),
        isUbo: true,
        ownershipPercentage:
          !isIncorporatedTrustee &&
          data.ubo.ownershipPercentage != null &&
          data.ubo.ownershipPercentage !== ""
            ? Number(data.ubo.ownershipPercentage)
            : null,
      });
    }

    if (!data.isUboDirector || (data.directors && data.directors.length > 0)) {
      data.directors?.forEach((director) => {
        if (director.name || director.email) {
          payload.push({
            ...(director.id ? { id: director.id } : {}),
            businessId,
            name: director.name,
            email: director.email,
            phone: director.phone || null,
            dob: director.dateOfBirth ? formatDate(director.dateOfBirth) : null,
            isDirector: true,
            isUbo: false,
            ownershipPercentage: null,
          });
        }
      });
    }

    const action = await dispatch(addBusinessDirector(payload));
    if (addBusinessDirector.fulfilled.match(action)) {
      dispatch(updateDirectorsList(action.payload));
      handleNext();
    } else if (addBusinessDirector.rejected.match(action)) {
      onShowErrorAlert("", String(action.payload));
    }
  };

  const getSavedDirectorsDetails = async () => {
    const stepNumber = 3;
    const action = await dispatch(
      getParticularStep({ businessId, stepNumber })
    );

    if (getParticularStep.fulfilled.match(action)) {
      const data = action?.payload;
      if (Array.isArray(data) && data.length > 0) {
        const uboParty = data.find((item: any) => item.isUbo);
        const isUboDirectorVal = Boolean(uboParty?.isDirector);
        const otherDirectors = uboParty
          ? data.filter(
              (item: any) => item !== uboParty && (item.isDirector || !item.isUbo)
            )
          : data.filter((item: any) => item.isDirector || !item.isUbo);

        reset({
          ubo: {
            id: uboParty?.id || null,
            name: uboParty?.name || "",
            email: uboParty?.email || "",
            phone: uboParty?.phone || "",
            dateOfBirth: uboParty?.dob ? dayjs(uboParty.dob) : null,
            ownershipPercentage: uboParty?.ownershipPercentage || "",
          },
          isUboDirector: isUboDirectorVal,
          directors:
            otherDirectors.length > 0
              ? otherDirectors.map((dir: any) => ({
                id: dir.id || null,
                name: dir.name || "",
                email: dir.email || "",
                dateOfBirth: dir.dob ? dayjs(dir.dob) : null,
              }))
              : !isUboDirectorVal
                ? [{ id: null, name: "", email: "", dateOfBirth: null }]
                : [],
        });
        return;
      }
    }

    reset({
      ubo: {
        id: null,
        name: "",
        email: "",
        phone: "",
        dateOfBirth: null,
        ownershipPercentage: "",
      },
      isUboDirector: false,
      directors: [{ id: null, name: "", email: "", dateOfBirth: null }],
    });
  };

  useEffect(() => {
    getSavedDirectorsDetails();
  }, []);

  const isIncorporatedTrustee =
    selectedBusinessType?.label?.toLowerCase() === "incorporated trustees/ngo";

  const initialDirectorSectionDetails = {
    header: "Associated parties",
    headerSubline:
      "Directors and Ultimate beneficial owners (anyone that holds a minimum of 20% shares)",
  };

  const directorSectionDetails = isIncorporatedTrustee
    ? {
        ...initialDirectorSectionDetails,
        header: "Trustee member's details",
        headerSubline:
          "Trustees govern organizational affairs, hold assets, and acts on its behalf",
      }
    : initialDirectorSectionDetails;

  const validateOwnershipPercentage = (value: any) => {
    const strVal = String(value ?? "").trim();
    if (!strVal) return "Ownership percentage is required";
    if (/^0/.test(strVal)) return "Ownership percentage cannot start with 0";
    if (/^-/.test(strVal)) return "Ownership percentage cannot be negative";
    const num = Number(strVal);
    if (isNaN(num) || num <= 0) return "Ownership percentage must be greater than 0";
    if (num > 100) return "Ownership percentage cannot exceed 100%";
    return true;
  }

  const renderPartyFields = (prefix: string, isUbo: boolean) => (
    <>
      <FormSection>
        <Controller
          name={`${prefix}.name` as any}
          control={control}
          rules={{ required: "Name is required" }}
          render={({ field }) => (
            <>
              <Input
                height="48px"
                label={isUbo ? "Beneficiary's name" : "Director's name"}
                {...field}
                hasError={!!getNestedError(errors, `${prefix}.name`)}
              />
              {getNestedError(errors, `${prefix}.name`) && (
                <span className="error">
                  {getNestedError(errors, `${prefix}.name`)?.message}
                </span>
              )}
            </>
          )}
        />
      </FormSection>

      <FormSection>
        <Controller
          name={`${prefix}.email` as any}
          control={control}
          rules={{
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          }}
          render={({ field }) => (
            <>
              <Input
                height="48px"
                label={isUbo ? "Beneficiary's email" : "Director's email"}
                {...field}
                hasError={!!getNestedError(errors, `${prefix}.email`)}
              />
              {getNestedError(errors, `${prefix}.email`) && (
                <span className="error">
                  {getNestedError(errors, `${prefix}.email`)?.message}
                </span>
              )}
            </>
          )}
        />
      </FormSection>

      {isUbo && (
        <FormSection>
          <Controller
            name={`${prefix}.phone` as any}
            control={control}
            rules={{ required: "Phone number is required" }}
            render={({ field }) => (
              <InputPhone
                label="Phone number"
                placeholder="Enter phone number"
                field={field}
                height="48px"
                defaultCountry={businessCountry}
              />
            )}
          />
          {getNestedError(errors, `${prefix}.phone`) && (
            <span className="error">
              {getNestedError(errors, `${prefix}.phone`)?.message}
            </span>
          )}
        </FormSection>
      )}

      <FormSection>
        <Controller
          name={`${prefix}.dateOfBirth` as any}
          control={control}
          rules={{ required: "Date of birth is required" }}
          render={({ field }) => (
            <>
              <CustomDatePicker
                lightBorder={true}
                {...field}
                placeholder="Date of birth"
                disabledDate={isUnderage}
              />
              {getNestedError(errors, `${prefix}.dateOfBirth`) && (
                <span className="error">
                  {getNestedError(errors, `${prefix}.dateOfBirth`)?.message}
                </span>
              )}
            </>
          )}
        />
      </FormSection>

      {isUbo && !isIncorporatedTrustee && (
        <FormSection>
          <Controller
            name={`${prefix}.ownershipPercentage` as any}
            control={control}
            rules={{
              required: "Ownership percentage is required",
              validate: validateOwnershipPercentage,
            }}
            render={({ field }) => (
              <>
                <Input
                  height="48px"
                  label="Ownership percentage"
                  type="number"
                  {...field}
                  hasError={
                    !!getNestedError(errors, `${prefix}.ownershipPercentage`)
                  }
                />
                {getNestedError(errors, `${prefix}.ownershipPercentage`) && (
                  <span className="error">
                    {
                      getNestedError(errors, `${prefix}.ownershipPercentage`)
                        ?.message
                    }
                  </span>
                )}
              </>
            )}
          />
        </FormSection>
      )}
    </>
  );

  return (
    <OverallContainer>
      <Loader isLoading={loading} />
      <PageHeaderWrapper>
        <BusinessTitle>{directorSectionDetails.header}</BusinessTitle>
        <BusinessText>{directorSectionDetails.headerSubline}</BusinessText>
      </PageHeaderWrapper>

      <div>
        <IconWrapper>
          <DirectorIndex>Ultimate beneficiary owner</DirectorIndex>
        </IconWrapper>
        {renderPartyFields("ubo", true)}
      </div>

      <IconWrapper>
        <DirectorIndex>{isIncorporatedTrustee ? "Trustee 1" : "Director 1"}</DirectorIndex>
      </IconWrapper>

      <FormSection>
        <Acknowledgement>
          <Controller
            name="isUboDirector"
            control={control}
            render={({ field }) => (
              <input
                type="checkbox"
                checked={field.value}
                onChange={(e) => {
                  const checked = e.target.checked;
                  field.onChange(checked);
                  if (!checked && fields.length === 0) {
                    append({ name: "", email: "", dateOfBirth: null });
                  } else if (checked && fields.length > 2) {
                    const indicesToRemove = Array.from(
                        { length: fields.length - 2 },
                        (_, i) => fields.length - 1 - i
                    );
                    remove(indicesToRemove);
                  }
                }}
              />
            )}
          />
          <p>Use the ultimate beneficiary owner details above</p>
        </Acknowledgement>
      </FormSection>

      {fields.map((director, index) => {
        const directorNumber = isUboDirector ? index + 2 : index + 1;
        const showDelete = isUboDirector || fields.length > 1;

        return (
          <div key={director.id || index}>
            {(isUboDirector || index > 0) && (
              <IconWrapper>
                <DirectorIndex>{isIncorporatedTrustee ? `Trustee ${directorNumber}` : `Director ${directorNumber}`}</DirectorIndex>
                {showDelete && (
                  <button type="button" onClick={() => handleDeleteDirector(index)}>
                    <img src={DeleteIcon} alt="delete-icon" />
                  </button>
                )}
              </IconWrapper>
            )}

            {!isUboDirector && index === 0 && showDelete && (
              <IconWrapper style={{ justifyContent: "flex-end" }}>
                <button type="button" onClick={() => handleDeleteDirector(index)}>
                  <img src={DeleteIcon} alt="delete-icon" />
                </button>
              </IconWrapper>
            )}

            {renderPartyFields(`directors.${index}`, false)}
          </div>
        );
      })}

      {fields.length < (isUboDirector ? 2 : 3) && (
        <AddButton type="button" onClick={addDirector}>
          <img src={PlusIcon} alt="plus-icon" />
          Add another {isIncorporatedTrustee ? "trustee" : "director"}
        </AddButton>
      )}

      <OnboardingButtonContainer>
        <Button
          label="Back"
          theme="alternate"
          height="48px"
          fontSize="16px"
          width="150px"
          onClick={handlePrevious}
        />
        <Button
          label="Continue"
          width={width < 1150 ? "" : "494px"}
          height="48px"
          fontSize="16px"
          type="button"
          onClick={handleSubmit(handleAddDirector)}
          disabled={!isValid || loading}
        />
      </OnboardingButtonContainer>
    </OverallContainer>
  );
};

export default Director;
